import { ThreeEvent } from '@react-three/fiber';
import React, { useCallback } from 'react';
import { Vector3 } from 'three';
import { ISelectedModel2D, ITexture } from '../../@types/ContextProviderRelated';
import { EInteractionMode } from '../../@types/Enums';
import { IModel2D } from '../../models/standard/Model2DModel';
import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { useMultiSelectToggleContext } from '../../provider/MultiSelectToggleProvider';
import { useToolbarContext } from '../../provider/ToolbarProvider';
import { setItemFootprint } from '../../utils/itemFootprintRegistry';
import SVGLoader from './SVGLoader';

interface Model2DProps {
  model: IModel2D;
  svgPath: string;
  position?: [number, number, number];
  length: number;
  depth: number;
  fillColor?: string;
  texture?: ITexture;
  rotation: number;
  selectedModel2D: ISelectedModel2D;
  index: number;
  onClick: (e: ThreeEvent<PointerEvent>) => void;
  onPositionChange?: (newPosition: [number, number, number]) => void;
  onDragMove?: (newPosition: [number, number, number]) => void;
  onRotationChange?: (newRotation: number) => void;
  onDragEnd?: (finalPosition: Vector3) => void;
  isGhost?: boolean; // NEW PROP
}

const Model2D: React.FC<Model2DProps> = ({
  model,
  svgPath,
  position = [0, 0, 0],
  length,
  depth,
  fillColor,
  texture,
  rotation,
  selectedModel2D,
  index,
  onClick,
  onPositionChange,
  onDragMove,
  onRotationChange,
  onDragEnd,
  isGhost = false
}) => {
  const { interactionMode, setInteractionMode } = useToolbarContext();
  const { applyTexture } = useBuilding();
  const { showRoomComponentDetails } = useMultiSelectToggleContext();
  const isSelected = selectedModel2D.selectedModel2DIndex === index;
  const handleFootprintChange = useCallback(({ width, length: footprintLength }: { width: number; length: number }) => {
    setItemFootprint(model, { width, length: footprintLength });
  }, [model]);
  
  const handleModel2dClick = (event: ThreeEvent<PointerEvent>, isDragging: boolean) => {
    // If it's a ghost, ignore clicks immediately
    if (isGhost) return;

    if (event.button !== 0 || isDragging) return;
    event.stopPropagation();
    if (interactionMode === EInteractionMode.SET_TEXTURE) {
      applyTexture(model);
      setInteractionMode(EInteractionMode.NONE);
      onClick(event);
    } else {
      onClick(event);
    }
  };

  return (
    <SVGLoader
      svgPath={svgPath}
      position={position}
      length={length}
      depth={depth}
      rotation={rotation}
      fillColor={fillColor}
      texture={texture}
      index={index}
      isSelected={isSelected}
      interactionMode={interactionMode}
      showRoomComponentDetails={showRoomComponentDetails}
      onClick={onClick}
      onPositionChange={onPositionChange}
      onDragMove={onDragMove}
      onRotationChange={onRotationChange}
      customHandleClick={handleModel2dClick}
      onDragEnd={onDragEnd}
      onFootprintChange={handleFootprintChange}
      isGhost={isGhost}
    />
  );
};

export default Model2D;
